import { useQuery } from '@tanstack/react-query';
import { useAppAuth } from './useAppAuth';
import { createApiClient, trafficNewsService } from '../services/api';
import type { TrafficLocation } from './useDashboardLocation';
import type { TrafficIncident } from '../types';

// Unions incidents across every location passed in (e.g. a carrier's several Job Alert areas,
// each with its own configured radius — see useDashboardLocation) and de-dupes by incident id,
// since the same DATEX II record can fall within range of more than one alert area. Mirrors the
// mobile app's useTrafficNews hook (mobile/src/hooks/useTrafficNews.ts).
//
// `enabled` (default true) additionally gates the fetch — UserDashboardPage passes "has Local
// Info been scrolled into view yet" so this doesn't fire on every dashboard load.
export function useTrafficNews(locations: TrafficLocation[], enabled = true) {
  const { getToken } = useAppAuth();
  const key = locations.map(l => `${l.lat.toFixed(2)},${l.lng.toFixed(2)},${l.radiusMiles}`).join('|');

  return useQuery({
    queryKey: ['traffic-news-dash', key],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const lists = await Promise.all(
        locations.map(l => trafficNewsService(client).getIncidents(l.lat, l.lng, l.radiusMiles))
      );
      const seen = new Set<string>();
      const merged: TrafficIncident[] = [];
      for (const list of lists) {
        for (const incident of list) {
          if (seen.has(incident.id)) continue;
          seen.add(incident.id);
          merged.push(incident);
        }
      }
      return merged.sort((a, b) => a.distanceMiles - b.distanceMiles);
    },
    enabled: enabled && locations.length > 0,
    staleTime: 5 * 60_000,
  });
}
